import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useScopeCheck } from '../../../../../../reusable-components/scopes';

export const useVendorFields = ({
  setValue,
  invoiceItemData,
  autoVendors,
  manualVendors,
  setUnsavedData,
  bookingTypeId,
}) => {
  const [selectedVendor, setSelectedVendor] = useState(null);

  const vendorOptions = useVendorOptions({
    manualInvoice: invoiceItemData?.invoice?.sourceMethod !== null,
    autoVendors,
    manualVendors,
    bookingTypeId,
  });

  const buildVendorFields = (vendorId) => {
    const vendor = vendorOptions.find((v) => v.value === vendorId);
    return {
      id: vendor?.value ?? 0,
      name: vendor?.label ?? '',
      address: [vendor?.address, vendor?.secondAddressLine].filter(Boolean).join(', '),
      phone: vendor?.phone ?? '',
      bookingCode: vendor?.bookingCode ?? '',
      bookingTypeId: vendor?.bookingTypeId ?? '',
    };
  };

  const canAddVendor = useScopeCheck(['View-Invoice-Vendors', 'Edit-Invoice-Vendors'], true);

  const updateVendorValue = (newValue) => {
    const vendor = buildVendorFields(newValue?.value);
    setValue('vendorId', vendor.id);
    setValue('vendorName', vendor.name);
    setSelectedVendor(vendor);
    setUnsavedData((prev) => ({ ...prev, vendorId: newValue?.value, vendorName: newValue?.label }));
  };

  // Self-initialize selectedVendor once the invoice's original vendorId and the
  // vendor options list are both available — handles either loading first.
  useEffect(() => {
    const originalVendorId = invoiceItemData?.invoice?.vendorId;
    if (!originalVendorId || vendorOptions.length === 0) return;
    setSelectedVendor(buildVendorFields(originalVendorId));
  }, [invoiceItemData?.invoice?.vendorId, vendorOptions]);

  const vendorTooltipText = () => {
    if (!selectedVendor?.address && !selectedVendor?.phone) return '';
    return (
      <Box>
        {selectedVendor.address && (
          <>
            Address: {selectedVendor.address}
            {selectedVendor.phone && <br />}
          </>
        )}
        {selectedVendor.phone && <>Phone: {selectedVendor.phone}</>}
      </Box>
    );
  };

  return {
    buildVendorFields,
    updateVendorValue,
    vendorOptions,
    vendorTooltipText,
    canAddVendor,
    selectedVendor,
  };
};
export const useVendorOptions = ({ manualInvoice, autoVendors, manualVendors, bookingTypeId }) =>
  useMemo(
    () => (manualInvoice ? manualVendors.filter((v) => v.bookingTypeId === bookingTypeId) : autoVendors),
    [autoVendors, manualVendors, manualInvoice, bookingTypeId]
  );
